import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Empty,
  Image,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  BarcodeOutlined,
  ClearOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  FilterOutlined,
  HistoryOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  RestOutlined,
  SearchOutlined,
  ShopOutlined,
  SwapOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import config from "../config";
import "./RTOInventoryPage.css";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const LOCATION_META = {
  "Msr Ashram Store": { color: "#2563eb", icon: <ShopOutlined />, short: "Ashram" },
  "Customer Lounge": { color: "#0891b2", icon: <TeamOutlined />, short: "Lounge" },
  "Courier Area": { color: "#7c3aed", icon: <EnvironmentOutlined />, short: "Courier" },
  "RTO Inventory": { color: "#475569", icon: <InboxOutlined />, short: "Inventory" },
  "RTO Staging": { color: "#d97706", icon: <InboxOutlined />, short: "Staging" },
  "RTO-repacking": { color: "#ea580c", icon: <InboxOutlined />, short: "Repack" },
  "RTO-damaged": { color: "#dc2626", icon: <InboxOutlined />, short: "Damaged" },
  "RTO-scrap": { color: "#6b7280", icon: <InboxOutlined />, short: "Scrap" },
  Unassigned: { color: "#94a3b8", icon: <AppstoreOutlined />, short: "Unassigned" },
};

const BASKET_CONFIG = {
  good: { label: "Good", color: "#16803c", bg: "rgba(22,128,60,0.08)" },
  repacking: { label: "Repacking", color: "#d97706", bg: "rgba(217,119,6,0.08)" },
  damaged: { label: "Damaged", color: "#dc2626", bg: "rgba(220,38,38,0.08)" },
  scrap: { label: "Scrap", color: "#6b7280", bg: "rgba(107,114,128,0.08)" },
};

const MOVEMENT_TYPES = {
  assign: { label: "Assigned", color: "blue", icon: <InboxOutlined /> },
  transfer: { label: "Transferred", color: "purple", icon: <SwapOutlined /> },
  return: { label: "Returned", color: "orange", icon: <RestOutlined /> },
  adjust: { label: "Adjusted", color: "cyan", icon: <InfoCircleOutlined /> },
};

const PRESETS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "All time", value: "all" },
];

const ALL_LOCATIONS = [
  "Msr Ashram Store",
  "Customer Lounge",
  "Courier Area",
  "RTO Staging",
  "RTO Inventory",
];

const computePreset = (preset) => {
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  switch (preset) {
    case "today":
      return { startDate: startOfToday.toISOString(), endDate: new Date().toISOString() };
    case "yesterday": {
      const y = new Date(startOfToday);
      y.setDate(y.getDate() - 1);
      return { startDate: y.toISOString(), endDate: new Date(startOfToday).toISOString() };
    }
    case "7d": {
      const s = new Date(startOfToday);
      s.setDate(s.getDate() - 6);
      return { startDate: s.toISOString(), endDate: new Date().toISOString() };
    }
    case "30d": {
      const s = new Date(startOfToday);
      s.setDate(s.getDate() - 29);
      return { startDate: s.toISOString(), endDate: new Date().toISOString() };
    }
    default: return {};
  }
};

const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) return null;
  const days = moment(expiryDate).diff(moment(), "days");
  if (days < 0) return { color: "#dc2626", bg: "rgba(220,38,38,0.08)", label: "Expired" };
  if (days <= 7) return { color: "#dc2626", bg: "rgba(220,38,38,0.08)", label: `${days}d left` };
  if (days <= 30) return { color: "#d97706", bg: "rgba(217,119,6,0.08)", label: `${days}d left` };
  return { color: "#16a34a", bg: "rgba(22,163,74,0.08)", label: `${days}d left` };
};

const getCookieUser = () => ({
  name: Cookies.get("userName") || Cookies.get("userEmail") || "RTO Officer",
  email: Cookies.get("userEmail") || "",
});

// Check if a product can be transferred (not Completed, not Damaged, not Scrap)
const canTransfer = (statuses, basketTypes) => {
  const s = statuses || ["Received"];
  if (s.includes("Completed")) return false;
  const b = basketTypes || [];
  if (b.includes("damaged") || b.includes("scrap")) return false;
  return true;
};

/* ── TRANSFER MODAL ── */
const TransferModal = ({ visible, onClose, onSuccess, selectedItems, fromLocation }) => {
  const [toLocation, setToLocation] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTransfer = async () => {
    if (!toLocation) { message.warning("Select a destination"); return; }
    setLoading(true);
    try {
      const user = getCookieUser();
      await axios.post(`${config.apiBaseUrl}/rto/inventory/transfer`, {
        items: selectedItems.map((item) => ({ ...item, quantity: item.totalQuantity || item.quantity })),
        fromLocation: fromLocation || "",
        toLocation,
        movedBy: user.name,
        movedByEmail: user.email,
        reason: reason || "Location transfer",
      });
      message.success(`${selectedItems.length} item(s) transferred`);
      onSuccess(); onClose(); setToLocation(""); setReason("");
    } catch (err) { message.error(err.response?.data?.message || "Transfer failed"); }
    finally { setLoading(false); }
  };

  return (
    <Modal
      title={<span style={{ display: "flex", alignItems: "center", gap: 8 }}><SwapOutlined /> Transfer Products</span>}
      open={visible}
      onCancel={() => { onClose(); setToLocation(""); setReason(""); }}
      onOk={handleTransfer} confirmLoading={loading} okText="Transfer"
      okButtonProps={{ disabled: !toLocation }} width={520} className="rto-inv-modal"
    >
      <div className="rto-inv-modal-body">
        <div className="rto-inv-modal-info">
          <InfoCircleOutlined />
          <Text>Transfer {selectedItems.length} item(s)
            {fromLocation ? <Text type="secondary"> from <Tag>{fromLocation}</Tag></Text> : ""}
          </Text>
        </div>
        <div className="rto-inv-modal-selected">
          {selectedItems.map((item, i) => (
            <Tag key={i} color="blue" style={{ marginBottom: 4 }}>
              {item.name || item.sku} × {item.totalQuantity || item.quantity}
            </Tag>
          ))}
        </div>
        <div className="rto-inv-modal-field">
          <label>Destination Location *</label>
          <Select placeholder="Select destination" value={toLocation || undefined}
            onChange={(v) => setToLocation(v)} style={{ width: "100%" }}
            options={ALL_LOCATIONS.filter((l) => l !== fromLocation).map((l) => ({ label: l, value: l }))}
          />
        </div>
        <div className="rto-inv-modal-field">
          <label>Reason</label>
          <Input.TextArea rows={2} placeholder="Optional reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
};

/* ── RETURN MODAL ── */
const ReturnModal = ({ visible, onClose, onSuccess, selectedItems }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleReturn = async () => {
    setLoading(true);
    try {
      const user = getCookieUser();
      await axios.post(`${config.apiBaseUrl}/rto/inventory/return-to-staging`, {
        items: selectedItems.map((item) => ({ ...item, quantity: item.totalQuantity || item.quantity })),
        movedBy: user.name, movedByEmail: user.email,
        reason: reason || "Returned to RTO staging",
      });
      message.success(`${selectedItems.length} item(s) returned to RTO Staging`);
      onSuccess(); onClose(); setReason("");
    } catch (err) { message.error(err.response?.data?.message || "Return failed"); }
    finally { setLoading(false); }
  };

  return (
    <Modal
      title={<span style={{ display: "flex", alignItems: "center", gap: 8 }}><RestOutlined /> Return to RTO Staging</span>}
      open={visible} onCancel={() => { onClose(); setReason(""); }}
      onOk={handleReturn} confirmLoading={loading} okText="Return" okButtonProps={{ danger: true }} width={520} className="rto-inv-modal"
    >
      <div className="rto-inv-modal-body">
        <div className="rto-inv-modal-info rto-inv-modal-warn">
          <WarningOutlined />
          <Text>Return {selectedItems.length} item(s) back to RTO Staging?</Text>
        </div>
        <div className="rto-inv-modal-selected">
          {selectedItems.map((item, i) => (
            <Tag key={i} color="orange" style={{ marginBottom: 4 }}>
              {item.name || item.sku} × {item.totalQuantity || item.quantity} [{item.location}]
            </Tag>
          ))}
        </div>
        <div className="rto-inv-modal-field">
          <label>Reason</label>
          <Input.TextArea rows={2} placeholder="Optional reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
};

/* ── MAIN ── */
const RTOInventoryPage = ({ darkTheme = false, isMobile = false }) => {
  const [data, setData] = useState({ totals: {}, locations: [], products: [], rawItems: [] });
  const [movements, setMovements] = useState([]);
  const [movementsTotal, setMovementsTotal] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [locationFilter, setLocationFilter] = useState(null);
  const [basketFilter, setBasketFilter] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [activeTab, setActiveTab] = useState("inventory");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [transferVisible, setTransferVisible] = useState(false);
  const [returnVisible, setReturnVisible] = useState(false);
  const [movTypeFilter, setMovTypeFilter] = useState(null);
  const [movSearch, setMovSearch] = useState("");
  const [movPage, setMovPage] = useState(1);
  const [showAllActivity, setShowAllActivity] = useState(false);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range && range.length === 2) {
        params.startDate = range[0].startOf("day").toISOString();
        params.endDate = range[1].endOf("day").toISOString();
      } else {
        const p = computePreset(preset);
        if (p.startDate) params.startDate = p.startDate;
        if (p.endDate) params.endDate = p.endDate;
      }
      if (searchText.trim()) params.search = searchText.trim();
      if (locationFilter) params.location = locationFilter;
      if (basketFilter) params.basket = basketFilter;
      if (orderSearch.trim()) params.orderID = orderSearch.trim();
      params.limit = 2000;
      const res = await axios.get(`${config.apiBaseUrl}/rto/inventory/all`, { params });
      setData(res.data || { totals: {}, locations: [], products: [], rawItems: [] });
    } catch (err) { message.error(err.response?.data?.message || "Failed to load inventory"); }
    finally { setLoading(false); }
  }, [preset, range, searchText, locationFilter, basketFilter, orderSearch]);

  const fetchMovements = useCallback(async (page = 1) => {
    setMovementsLoading(true);
    try {
      const params = { limit: 30, page };
      if (showAllActivity) params.all = "true";
      if (movTypeFilter) params.type = movTypeFilter;
      if (movSearch.trim()) params.search = movSearch.trim();
      const res = await axios.get(`${config.apiBaseUrl}/rto/inventory/movements`, { params });
      setMovements(res.data?.data || []);
      setMovementsTotal(res.data?.total || 0);
      setRecentActivity(res.data?.recentActivity || []);
    } catch (err) {}
    finally { setMovementsLoading(false); }
  }, [movTypeFilter, movSearch, showAllActivity]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);
  useEffect(() => { if (activeTab === "activity") fetchMovements(movPage); }, [activeTab, movPage, fetchMovements]);

  const totals = data.totals || {};
  const locations = data.locations || [];
  const products = data.products || [];
  const basketCounts = totals.basketCounts || {};

  const selectedItems = useMemo(
    () => selectedRowKeys.map((key) => products.find((p) => `${p.sku || p.productId || p.name}__${p.name}` === key)).filter(Boolean),
    [selectedRowKeys, products]
  );

  const locationOptions = useMemo(
    () => [...new Set([...ALL_LOCATIONS, ...locations.map((l) => l.location)])].map((k) => ({ label: k, value: k })),
    [locations]
  );

  // Determine which actions are available for selected items
  const selectedCanTransfer = useMemo(
    () => selectedItems.length > 0 && selectedItems.every((item) => canTransfer(item.transferStatuses, item.basketTypes)),
    [selectedItems]
  );
  const selectedHasCompleted = useMemo(
    () => selectedItems.some((item) => (item.transferStatuses || ["Received"]).includes("Completed")),
    [selectedItems]
  );
  const selectedHasNonCompleted = useMemo(
    () => selectedItems.some((item) => !(item.transferStatuses || ["Received"]).every((s) => s === "Completed")),
    [selectedItems]
  );

  const clearFilters = () => {
    setPreset("7d"); setRange(null); setSearchText(""); setLocationFilter(null); setBasketFilter(null); setOrderSearch("");
  };

  const markComplete = async () => {
    if (!selectedItems.length) return;
    try {
      const user = getCookieUser();
      await axios.post(`${config.apiBaseUrl}/rto/inventory/mark-complete`, {
        items: selectedItems.filter((item) => !(item.transferStatuses || ["Received"]).every((s) => s === "Completed"))
          .map((item) => ({ ...item, quantity: item.totalQuantity || item.quantity })),
        movedBy: user.name, movedByEmail: user.email,
      });
      message.success("Items marked as completed"); setSelectedRowKeys([]); fetchInventory();
    } catch (err) { message.error(err.response?.data?.message || "Failed"); }
  };

  const exportCsv = () => {
    if (!products.length) { message.info("No products"); return; }
    const header = ["#", "Product", "SKU", "Batch", "Qty", "Locations", "Basket", "Status", "Expiry", "Orders", "Received"];
    const rows = products.map((p, i) => [
      i + 1, p.name, p.sku || "", (p.batchNumbers || []).join("; "), p.totalQuantity,
      (p.locations || []).join("; "), (p.basketTypes || []).map((b) => BASKET_CONFIG[b]?.label || b).join("; "),
      (p.transferStatuses || ["Received"]).join("; "),
      p.expiryDate ? moment(p.expiryDate).format("YYYY-MM-DD") : "",
      (p.orderIDs || []).join("; "), p.receivedAt ? moment(p.receivedAt).format("YYYY-MM-DD HH:mm") : "",
    ]);
    const csv = [header, ...rows].map((l) => l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.setAttribute("download", `rto-inventory-${moment().format("YYYYMMDD-HHmm")}.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
  };

  const summaryCards = [
    { key: "locations", label: "Locations", value: totals.locations || 0, suffix: "active", gradient: "linear-gradient(135deg,#2563eb,#4f46e5)", icon: <EnvironmentOutlined /> },
    { key: "orders", label: "Orders", value: totals.orders || 0, suffix: "RTOs", gradient: "linear-gradient(135deg,#0891b2,#06b6d4)", icon: <InboxOutlined /> },
    { key: "products", label: "Products", value: totals.products || 0, suffix: "items", gradient: "linear-gradient(135deg,#7c3aed,#a855f7)", icon: <AppstoreOutlined /> },
    { key: "quantity", label: "Quantity", value: totals.quantity || 0, suffix: "units", gradient: "linear-gradient(135deg,#16803a,#22c55e)", icon: <ShopOutlined /> },
  ];

  const basketCards = [
    { key: "good", ...BASKET_CONFIG.good, count: basketCounts.good || 0 },
    { key: "repacking", ...BASKET_CONFIG.repacking, count: basketCounts.repacking || 0 },
    { key: "damaged", ...BASKET_CONFIG.damaged, count: basketCounts.damaged || 0 },
    { key: "scrap", ...BASKET_CONFIG.scrap, count: basketCounts.scrap || 0 },
  ];

  const renderStatus = (statuses) => {
    const unique = [...new Set(statuses || ["Received"])];
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {unique.map((s) => {
          if (s === "Completed") return <Tag key={s} color="success" icon={<CheckCircleOutlined />}>Completed</Tag>;
          if (s === "Transferred") return <Tag key={s} color="processing" icon={<SwapOutlined />}>Transferred</Tag>;
          return <Tag key={s} color="default" icon={<ClockCircleOutlined />}>Received</Tag>;
        })}
      </div>
    );
  };

  const inventoryColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 55,
      render: (_, __, index) => <Text type="secondary" style={{ fontSize: 12 }}>{(25 * (movPage || 0)) + index + 1}</Text>,
    },
    {
      title: "Product",
      key: "product",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (_, p) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Image width={36} height={36} preview={false} src={p.imageUrl}
            fallback="https://via.placeholder.com/36?text=No+Img"
            style={{ borderRadius: 8, objectFit: "cover", background: "#f1f5f9" }}
          />
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ display: "block", fontSize: 13 }} ellipsis>{p.name || "—"}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              <BarcodeOutlined /> {p.sku || p.productId || "—"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Batch",
      key: "batch",
      width: 160,
      render: (_, p) => {
        const batches = p.batchNumbers || [];
        if (batches.length === 0) return <Text type="secondary">—</Text>;
        return <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{batches.map((b, i) => <Tag key={i} color="blue">{b}</Tag>)}</div>;
      },
    },
    { title: "Qty", dataIndex: "totalQuantity", key: "totalQuantity", width: 70, align: "center",
      sorter: (a, b) => (a.totalQuantity || 0) - (b.totalQuantity || 0),
      render: (val) => <span className="rto-inv-qty-badge">{val || 0}</span>,
    },
    {
      title: "Basket", key: "basketType", width: 110,
      render: (_, p) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(p.basketTypes || []).map((t) => {
            const cfg = BASKET_CONFIG[t];
            return cfg ? <Tag key={t} style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30`, fontWeight: 600 }}>{cfg.label}</Tag> : null;
          })}
        </div>
      ),
    },
    {
      title: "Location", key: "location", width: 140,
      render: (_, p) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(p.locations || []).map((loc) => {
            const meta = LOCATION_META[loc] || LOCATION_META.Unassigned;
            return <Tag key={loc} style={{ background: `${meta.color}10`, color: meta.color, border: `1px solid ${meta.color}30`, fontWeight: 500, fontSize: 11 }}>{meta.short || loc}</Tag>;
          })}
        </div>
      ),
    },
    {
      title: "Expiry", key: "expiry", width: 100,
      sorter: (a, b) => { if (!a.expiryDate) return 1; if (!b.expiryDate) return -1; return new Date(a.expiryDate) - new Date(b.expiryDate); },
      render: (_, p) => {
        if (!p.expiryDate) return <Text type="secondary">—</Text>;
        const st = getExpiryStatus(p.expiryDate);
        return <Tooltip title={moment(p.expiryDate).format("DD MMM YYYY")}><Tag style={{ background: st.bg, color: st.color, border: `1px solid ${st.color}30`, fontWeight: 600, fontSize: 11 }}>{st.label}</Tag></Tooltip>;
      },
    },
    {
      title: "Orders", key: "orderIDs", width: 120,
      render: (_, p) => {
        const ids = p.orderIDs || [];
        if (ids.length === 0) return <Text type="secondary">—</Text>;
        if (ids.length <= 2) return ids.map((id) => <Tag key={id} style={{ fontSize: 10 }}>{id}</Tag>);
        return <Tooltip title={ids.join(", ")}><Tag style={{ fontSize: 10 }}>{ids[0]} +{ids.length - 1}</Tag></Tooltip>;
      },
    },
    {
      title: "Received", key: "receivedAt", width: 110,
      sorter: (a, b) => new Date(a.receivedAt || 0) - new Date(b.receivedAt || 0),
      render: (_, p) => p.receivedAt ? <Text type="secondary" style={{ fontSize: 11 }}>{moment(p.receivedAt).format("DD MMM YY")}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Status", key: "transferStatus", width: 130,
      filters: [
        { text: "Received", value: "Received" },
        { text: "Transferred", value: "Transferred" },
        { text: "Completed", value: "Completed" },
      ],
      onFilter: (value, record) => {
        const statuses = record.transferStatuses || [record.transferStatus || "Received"];
        return statuses.includes(value);
      },
      render: (_, p) => renderStatus(p.transferStatuses || [p.transferStatus || "Received"]),
    },
  ];

  const movementColumns = [
    { title: "Type", key: "type", width: 100, render: (_, m) => { const cfg = MOVEMENT_TYPES[m.type] || {}; return <Tag color={cfg.color} icon={cfg.icon} style={{ fontSize: 11 }}>{cfg.label}</Tag>; } },
    { title: "Product", key: "product", render: (_, m) => <><Text strong style={{ fontSize: 12 }}>{m.productName || "—"}</Text><br /><Text type="secondary" style={{ fontSize: 10 }}>SKU: {m.sku}</Text></> },
    { title: "Qty", dataIndex: "quantity", key: "qty", width: 50, align: "center", render: (v) => <span className="rto-inv-qty-badge rto-inv-qty-sm">{v || 0}</span> },
    { title: "From", key: "fromLocation", width: 120, render: (_, m) => m.fromLocation ? <Tag style={{ fontSize: 10 }}>{m.fromLocation}</Tag> : "—" },
    { title: "", key: "arrow", width: 25, render: () => <ArrowRightOutlined style={{ color: "#94a3b8", fontSize: 11 }} /> },
    { title: "To", key: "toLocation", width: 120, render: (_, m) => m.toLocation ? <Tag color="blue" style={{ fontSize: 10 }}>{m.toLocation}</Tag> : "—" },
    { title: "Order", key: "orderID", width: 90, render: (_, m) => m.orderID ? <Tag style={{ fontSize: 10 }}>{m.orderID}</Tag> : "—" },
    { title: "By", key: "movedBy", width: 100, render: (_, m) => <Text type="secondary" style={{ fontSize: 11 }}>{m.movedBy || "—"}</Text> },
    { title: "When", key: "movedAt", width: 110, render: (_, m) => m.movedAt ? <Tooltip title={moment(m.movedAt).format("DD MMM YYYY HH:mm:ss")}><Text type="secondary" style={{ fontSize: 11 }}>{moment(m.movedAt).format("HH:mm")}</Text></Tooltip> : "—" },
  ];

  return (
    <div className={`rto-inv-container ${darkTheme ? "dark-theme" : ""}`}>
      {/* HERO */}
      <div className="rto-inv-hero">
        <div className="rto-inv-hero-left">
          <span className="rto-inv-hero-eyebrow">Inventory Management</span>
          <Title level={2} className="rto-inv-hero-title">RTO Inventory</Title>
        </div>
        <div className="rto-inv-hero-center">
          <Segmented value={preset} onChange={(v) => { setPreset(v); setRange(null); }} options={PRESETS} className="rto-inv-hero-segments" />
          <RangePicker value={range} onChange={(v) => setRange(v)} allowClear className="rto-inv-hero-range" placeholder={["Start", "End"]} />
        </div>
        <div className="rto-inv-hero-actions">
          <Button icon={<ReloadOutlined />} onClick={() => { fetchInventory(); if (activeTab === "activity") fetchMovements(); }}>Refresh</Button>
          <Button type="primary" icon={<ExportOutlined />} onClick={exportCsv}>Export CSV</Button>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="rto-inv-stat-grid">
        {summaryCards.map((s) => (
          <div key={s.key} className="rto-inv-stat-card" style={{ "--rto-inv-gradient": s.gradient }}>
            <div className="rto-inv-stat-head">
              <span className="rto-inv-stat-label">{s.label}</span>
              <span className="rto-inv-stat-icon" style={{ background: s.gradient }}>{s.icon}</span>
            </div>
            <div className="rto-inv-stat-value">{loading ? "—" : s.value}<span className="rto-inv-stat-suffix">{s.suffix}</span></div>
          </div>
        ))}
      </div>

      {/* BASKET STRIP */}
      <div className="rto-inv-basket-strip">
        {basketCards.map((b) => (
          <div key={b.key} className={`rto-inv-basket-pill ${basketFilter === b.key ? "active" : ""}`}
            style={{ "--pill-color": b.color, "--pill-bg": b.bg }}
            onClick={() => setBasketFilter(basketFilter === b.key ? null : b.key)}>
            <span className="rto-inv-basket-pill-label">{b.label}</span>
            <span className="rto-inv-basket-pill-count">{b.count}</span>
          </div>
        ))}
      </div>

      {/* MAIN CONTENT */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} className="rto-inv-main-tabs" items={[
        {
          key: "inventory",
          label: <span><AppstoreOutlined /> Inventory</span>,
          children: (
            <div className="rto-inv-layout">
              {/* FILTERS */}
              <aside className="rto-inv-filters">
                <div className="rto-inv-filters-header">
                  <FilterOutlined /><span>Filters</span>
                  <Button size="small" type="link" icon={<ClearOutlined />} onClick={clearFilters}>Clear</Button>
                </div>
                <div className="rto-inv-filter-group">
                  <label className="rto-inv-filter-label">Search</label>
                  <Input prefix={<SearchOutlined />} placeholder="Product, SKU, batch" allowClear value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>
                <div className="rto-inv-filter-group">
                  <label className="rto-inv-filter-label">Order ID</label>
                  <Input prefix={<BarcodeOutlined />} placeholder="Filter by order" allowClear value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} />
                </div>
                <div className="rto-inv-filter-group">
                  <label className="rto-inv-filter-label">Location</label>
                  <Select allowClear placeholder="All locations" value={locationFilter} onChange={(v) => setLocationFilter(v || null)} options={locationOptions} style={{ width: "100%" }} />
                </div>
                <div className="rto-inv-filter-group">
                  <label className="rto-inv-filter-label">Basket Type</label>
                  <Select allowClear placeholder="All types" value={basketFilter} onChange={(v) => setBasketFilter(v || null)}
                    options={Object.entries(BASKET_CONFIG).map(([k, v]) => ({ label: v.label, value: k }))} style={{ width: "100%" }} />
                </div>
                {locations.length > 0 && (
                  <div className="rto-inv-filter-section">
                    <label className="rto-inv-filter-label">Locations</label>
                    {locations.map((loc) => {
                      const meta = LOCATION_META[loc.location] || LOCATION_META.Unassigned;
                      return (
                        <div key={loc.location} className="rto-inv-loc-row" style={{ "--rto-loc-color": meta.color }}
                          onClick={() => setLocationFilter(locationFilter === loc.location ? null : loc.location)}>
                          <span className="rto-inv-loc-dot" />
                          <Text style={{ flex: 1, fontSize: 12 }}>{meta.short || loc.location}</Text>
                          <Tag style={{ fontSize: 11 }}>{loc.quantity}</Tag>
                        </div>
                      );
                    })}
                  </div>
                )}
              </aside>

              {/* TABLE */}
              <main className="rto-inv-content">
                <Spin spinning={loading}>
                  <div className="rto-inv-action-bar">
                    <Text type="secondary">{products.length} product{products.length === 1 ? "" : "s"} · {totals.quantity || 0} units</Text>
                    <div className="rto-inv-action-right">
                      {selectedRowKeys.length > 0 && (
                        <>
                          {selectedCanTransfer && (
                            <Button type="primary" icon={<SwapOutlined />} onClick={() => setTransferVisible(true)}>
                              Transfer ({selectedRowKeys.length})
                            </Button>
                          )}
                          {selectedHasNonCompleted && (
                            <Button icon={<CheckCircleOutlined />} style={{ color: "#16a34a", borderColor: "#16a34a" }} onClick={markComplete}>
                              Mark Complete
                            </Button>
                          )}
                          {selectedHasNonCompleted && (
                            <Popconfirm title="Return non-completed items to RTO Staging?" onConfirm={() => setReturnVisible(true)} okText="Yes" cancelText="No">
                              <Button danger icon={<RestOutlined />}>Return ({selectedRowKeys.length})</Button>
                            </Popconfirm>
                          )}
                          <Button onClick={() => setSelectedRowKeys([])}>Clear</Button>
                        </>
                      )}
                    </div>
                  </div>
                  {products.length === 0 ? (
                    <div className="rto-inv-empty"><Empty description={<div><Title level={4} style={{ marginTop: 8 }}>No inventory found</Title><Text type="secondary">Try different filters.</Text></div>} /></div>
                  ) : (
                    <Table className="rto-inv-table rto-inv-stock-table" size="small"
                      rowKey={(p) => `${p.sku || p.productId || p.name}__${p.name}`}
                      dataSource={products}
                      rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                      pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ["25", "50", "100"], showTotal: (t) => `${t} products` }}
                      columns={inventoryColumns} scroll={{ x: 1300 }}
                    />
                  )}
                </Spin>
              </main>
            </div>
          ),
        },
        {
          key: "activity",
          label: <span><HistoryOutlined /> Activity</span>,
          children: (
            <div className="rto-inv-activity">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Text strong>Today's Activity</Text>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button size="small" type={showAllActivity ? "default" : "primary"} onClick={() => { setShowAllActivity(false); setMovPage(1); }}>Today</Button>
                  <Button size="small" type={showAllActivity ? "primary" : "default"} onClick={() => { setShowAllActivity(true); setMovPage(1); }}>All Time</Button>
                </div>
              </div>
              {recentActivity.length > 0 && (
                <Card className="rto-inv-feed-card" title="Recent Activity" size="small">
                  <div className="rto-inv-feed" style={{ maxHeight: 200, overflowY: "auto" }}>
                    {recentActivity.map((m, i) => {
                      const cfg = MOVEMENT_TYPES[m.type] || {};
                      return (
                        <div key={m._id || i} className="rto-inv-feed-item">
                          <div className="rto-inv-feed-icon" style={{ color: cfg.color }}>{cfg.icon}</div>
                          <div className="rto-inv-feed-content">
                            <Text style={{ fontSize: 12 }}>
                              <strong>{m.productName}</strong> {m.quantity ? `× ${m.quantity}` : ""}
                              {" "}{m.type === "transfer" ? "→" : m.type === "return" ? "↩" : "→"}{" "}
                              {m.type === "return" ? m.toLocation : (m.fromLocation && m.toLocation ? `${m.fromLocation} → ${m.toLocation}` : m.toLocation)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 10 }}>{m.movedBy} · {moment(m.movedAt).fromNow()}</Text>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
              <Card className="rto-inv-movements-card" title="Movement History" size="small"
                extra={
                  <div style={{ display: "flex", gap: 6 }}>
                    <Input prefix={<SearchOutlined />} placeholder="Search" allowClear size="small" value={movSearch}
                      onChange={(e) => setMovSearch(e.target.value)} onPressEnter={() => fetchMovements(1)} style={{ width: 180 }} />
                    <Select allowClear placeholder="Type" size="small" value={movTypeFilter}
                      onChange={(v) => { setMovTypeFilter(v); setMovPage(1); }}
                      options={Object.entries(MOVEMENT_TYPES).map(([k, v]) => ({ label: v.label, value: k }))} style={{ width: 120 }} />
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchMovements(movPage)} />
                  </div>
                }>
                <Table className="rto-inv-table" size="small" rowKey={(m) => m._id} dataSource={movements} loading={movementsLoading}
                  pagination={{ current: movPage, total: movementsTotal, pageSize: 30, onChange: (p) => setMovPage(p), showTotal: (t) => `${t} movements` }}
                  columns={movementColumns} scroll={{ x: 1000 }} />
              </Card>
            </div>
          ),
        },
      ]} />

      <TransferModal visible={transferVisible} onClose={() => setTransferVisible(false)}
        onSuccess={() => { setSelectedRowKeys([]); fetchInventory(); }} selectedItems={selectedItems}
        fromLocation={selectedItems.length === 1 ? selectedItems[0].location : null} />
      <ReturnModal visible={returnVisible} onClose={() => setReturnVisible(false)}
        onSuccess={() => { setSelectedRowKeys([]); fetchInventory(); }} selectedItems={selectedItems} />
    </div>
  );
};

export default RTOInventoryPage;